import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API from '../api';
import { useLang } from '../LangContext';

/**
 * Group an array of orders by a key function.
 * Returns an array of { key, orders } objects sorted by key.
 */
function groupBy(orders, keyFn) {
  const map = {};
  for (const order of orders) {
    const key = keyFn(order) || 'Other';
    if (!map[key]) map[key] = [];
    map[key].push(order);
  }
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, orders]) => ({ key, orders }));
}

const STATUS_COLORS = {
  pending:   { bg: '#f9731620', color: '#f97316' },
  confirmed: { bg: '#22c55e20', color: '#22c55e' },
  rejected:  { bg: '#ef444420', color: '#ef4444' },
};

export default function BuyerDashboard() {
  const navigate = useNavigate();
  const { t } = useLang();

  const buyerToken = localStorage.getItem('buyerToken');
  const buyer      = (() => { try { const v = localStorage.getItem('buyer'); return v && v !== 'undefined' ? JSON.parse(v) : null; } catch { return null; } })();

  useEffect(() => {
    if (!buyerToken || !buyer) navigate('/login');
  }, [buyerToken, buyer, navigate]);

  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [groupMode, setGroupMode] = useState('category'); // 'category' | 'seller'

  useEffect(() => {
    if (!buyerToken || !buyer) return;
    API.defaults.headers.common['Authorization'] = `Bearer ${buyerToken}`;
    API.get('/bazaar/orders', { params: { buyer_id: buyer.id } })
      .then(r => setOrders(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError('Failed to load orders. Please try again.'))
      .finally(() => setLoading(false));
  }, [buyerToken, buyer?.id]);

  const logout = () => {
    localStorage.removeItem('buyerToken');
    localStorage.removeItem('buyer');
    navigate('/');
    window.location.reload();
  };

  // Group orders by selected mode
  const grouped = useMemo(() => {
    if (groupMode === 'seller') {
      return groupBy(orders, o => o.seller_name);
    }
    // Group by category: use the first item's category, or 'Other'
    return groupBy(orders, o => {
      const firstItem = o.items?.[0];
      return firstItem?.product_category || 'Other';
    });
  }, [orders, groupMode]);

  if (!buyerToken || !buyer) return null;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--primary)', padding: 0 }}>←</button>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 900, color: '#fff', marginBottom: 4 }}>
            {t.buyerDashboardTitle}
            </h1>
            <p style={{ color: '#ffffff', fontSize: 16 }}>
              {buyer.full_name} · {buyer.phone}
            </p>
          </div>
        </div>
        <button
          onClick={logout}
          style={{ padding: '9px 18px', background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440', borderRadius: 8, fontSize: 16, cursor: 'pointer' }}
        >
          {t.signOut}
        </button>
      </div>

      {/* Group mode toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
        <button
          onClick={() => setGroupMode('category')}
          style={{ padding: '8px 18px', borderRadius: 8, border: 'none', fontSize: 15, cursor: 'pointer', background: groupMode === 'category' ? '#f97316' : '#262626', color: groupMode === 'category' ? '#000' : '#ffffff', fontWeight: groupMode === 'category' ? 700 : 400 }}
        >
          {t.groupedByCategory}
        </button>
        <button
          onClick={() => setGroupMode('seller')}
          style={{ padding: '8px 18px', borderRadius: 8, border: 'none', fontSize: 15, cursor: 'pointer', background: groupMode === 'seller' ? '#f97316' : '#262626', color: groupMode === 'seller' ? '#000' : '#ffffff', fontWeight: groupMode === 'seller' ? 700 : 400 }}
        >
          {t.groupedBySeller}
        </button>
      </div>

      {/* Content */}
      {loading && (
        <p style={{ color: '#ffffff', textAlign: 'center', padding: 60 }}>{t.loading}</p>
      )}

      {!loading && error && (
        <div style={{ background: '#ef444415', border: '1px solid #ef444440', color: '#ef4444', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
          {error}
        </div>
      )}

      {!loading && !error && orders.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 24px', background: '#171717', border: '1px solid #262626', borderRadius: 16 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🛒</div>
          <p style={{ color: '#ffffff', fontSize: 16, marginBottom: 20 }}>{t.noPurchases}</p>
          <Link to="/bazaar" style={{ color: '#f97316', textDecoration: 'none', fontWeight: 700 }}>
            {t.bazaarTitle} →
          </Link>
        </div>
      )}

      {!loading && !error && grouped.map(({ key, orders: groupOrders }) => (
        <div key={key} style={{ marginBottom: 36 }}>
          {/* Group heading */}
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#f97316', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #262626' }}>
            {groupMode === 'seller' ? '🏪 ' : '📦 '}{key}
            <span style={{ color: '#ffffff', fontWeight: 400, fontSize: 15, marginLeft: 8 }}>
              ({groupOrders.length} order{groupOrders.length !== 1 ? 's' : ''})
            </span>
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {groupOrders.map(order => (
              <OrderCard key={order.id} order={order} t={t} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function OrderCard({ order, t }) {
  const [expanded, setExpanded] = useState(false);
  const statusStyle = STATUS_COLORS[order.status] || STATUS_COLORS.pending;

  return (
    <div style={{ background: '#171717', border: '1px solid #262626', borderRadius: 14, overflow: 'hidden' }}>
      {/* Order header */}
      <div
        style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setExpanded(v => !v)}
      >
        <div>
          <span style={{ fontWeight: 700, color: '#fff', fontSize: 15 }}>Order #{order.id}</span>
          <span style={{ color: '#ffffff', fontSize: 15, marginLeft: 12 }}>
            {order.seller_name}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ color: '#f97316', fontWeight: 700, fontSize: 15 }}>
            ETB {Number(order.total_amount).toLocaleString()}
          </span>
          <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: statusStyle.bg, color: statusStyle.color, fontWeight: 600 }}>
            {order.status}
          </span>
          <span style={{ color: '#ffffff', fontSize: 18 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded order details */}
      {expanded && (
        <div style={{ borderTop: '1px solid #262626', padding: '16px 20px' }}>
          {/* Payment info */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ background: '#0a0a0a', borderRadius: 8, padding: '8px 14px', fontSize: 15 }}>
              <span style={{ color: '#ffffff' }}>Payment: </span>
              <span style={{ color: '#fff', fontWeight: 600 }}>{order.payment_method?.toUpperCase()}</span>
            </div>
            <div style={{ background: '#0a0a0a', borderRadius: 8, padding: '8px 14px', fontSize: 15 }}>
              <span style={{ color: '#ffffff' }}>Proof: </span>
              <span style={{ color: '#fff', fontWeight: 600 }}>
                {order.proof_type === 'screenshot' ? '📷 Screenshot' : `Ref: ${order.proof_value}`}
              </span>
            </div>
          </div>

          {/* Order items */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(order.items || []).map((item, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', background: '#0a0a0a', borderRadius: 10, padding: '12px 14px' }}>
                {/* Photo placeholder */}
                <div style={{ width: 52, height: 52, background: '#262626', borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                  📦
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#fff', fontSize: 16, marginBottom: 2 }}>{item.product_name}</div>
                  {item.product_description && (
                    <div style={{ color: '#ffffff', fontSize: 12, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.product_description}
                    </div>
                  )}
                  {item.product_category && (
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: '#f9731620', color: '#f97316', fontWeight: 600 }}>
                      {item.product_category}
                    </span>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ color: '#ffffff', fontSize: 12 }}>× {item.quantity}</div>
                  <div style={{ color: '#f97316', fontWeight: 700, fontSize: 16 }}>
                    ETB {(Number(item.unit_price) * item.quantity).toLocaleString()}
                  </div>
                  <div style={{ color: '#737373', fontSize: 11 }}>
                    ETB {Number(item.unit_price).toLocaleString()} each
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Request delivery link for confirmed orders */}
          {order.status === 'confirmed' && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #262626' }}>
              <Link
                to="/bazaar/delivery"
                style={{ color: '#f97316', fontSize: 16, fontWeight: 700, textDecoration: 'none', borderBottom: '1px solid #f9731640', paddingBottom: 2 }}
              >
                🚚 {t.requestDelivery}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
